import { Box, Button, Flex, Spacer, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import usePeriodQueryParam from 'hooks/modules/period/usePeriodQueryParam';
import moment from 'moment';
import { Breakpoint } from 'presentation/theme/breakpoints';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useWindowSize } from 'react-use';
import AppRoute from 'router/AppRoute';
import { useCreateReportMutation } from 'rtkQuery/sharespro/queries/reports';
import { useFetchPortfolioQuery } from 'rtkQuery/sharespro/queries/portfolio';
import * as Yup from 'yup';

import FormDateInput from 'presentation/components/shared/form/FormDateInput';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import PortfolioPageLayout from 'presentation/layout/modules/portfolio/PortfolioPageLayout';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import AppCard from 'presentation/components/shared/card/AppCard';

interface FormValues {
  date_from: Date | null;
  date_to: Date | null;
  email: string;
}

const CreateReportPage: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [createReport, { isLoading }] = useCreateReportMutation();
  const [period] = usePeriodQueryParam();
  const accountId = id ? parseInt(id) : null;
  const { data } = useFetchPortfolioQuery({
    period,
    account_id: accountId,
  });

  const selectedAccount = data?.accounts.items.find(
    (item) => item.id === accountId
  );

  const minDate = selectedAccount?.dateOpen ?? new Date(2000);

  const formErrorHandler = useFormError<FormValues>();

  const validationSchema = Yup.object().shape({
    date_from: Yup.string().required(t('fieldIsRequired') ?? undefined),
    date_to: Yup.string().required(t('fieldIsRequired') ?? undefined),
    email: Yup.string()
      .required(t('fieldIsRequired') ?? undefined)
      .email(t('invalidEmail') ?? undefined),
  });

  const { width } = useWindowSize();

  const toast = useToast();

  const navigate = useNavigate();

  return (
    <PortfolioPageLayout>
      <Helmet>
        <title>{t('createReport')}</title>
      </Helmet>
      <GridHeading>{t('createReport')}</GridHeading>
      <AppCard p="40px" display="flex" flexDirection="column">
        <Formik<FormValues>
          enableReinitialize
          initialValues={{
            date_from: selectedAccount?.dateOpen ?? null,
            date_to: null,
            email: '',
          }}
          onSubmit={(values, formik) => {
            createReport({
              date_from: moment(values.date_from).format('DD.MM.YYYY'),
              date_to: moment(values.date_to).format('DD.MM.YYYY'),
              email: values.email,
              account_id: parseInt(id ?? '-1'),
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('reportSent'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.portfolio);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
          validationSchema={validationSchema}
        >
          {({ values, handleChange, setFieldValue }) => {
            const dateFromField = (
              <FormDateInput
                name="date_from"
                onChange={(date) => {
                  setFieldValue('date_from', date);
                }}
                selected={values.date_from}
                label={t('periodFromLabel')}
                minDate={minDate}
                maxDate={values.date_to ?? new Date()}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                scrollableYearDropdown
                yearDropdownItemNumber={50}
              />
            );

            const dateToField = (
              <FormDateInput
                name="date_to"
                onChange={(date) => {
                  setFieldValue('date_to', date);
                }}
                selected={values.date_to}
                label={t('periodToLabel')}
                minDate={values.date_from ?? minDate}
                maxDate={new Date()}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                scrollableYearDropdown
                yearDropdownItemNumber={50}
              />
            );

            return (
              <Form>
                {width >= Breakpoint.lg ? (
                  <Flex mb="40px">
                    <Box flex="1">{dateFromField}</Box>
                    <Spacer width="30px" maxWidth="30px" />
                    <Box flex="1">{dateToField}</Box>
                  </Flex>
                ) : (
                  <>
                    <Box mb="40px">{dateFromField}</Box>
                    <Box mb="40px">{dateToField}</Box>
                  </>
                )}
                <Box mb="40px">
                  <FormTextInput
                    name="email"
                    onChange={handleChange}
                    value={values.email}
                    type="email"
                    label={t('email')}
                  />
                </Box>
                <Button w="full" type="submit" isLoading={isLoading}>
                  {t('sendReportToEmail')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </PortfolioPageLayout>
  );
};

export default CreateReportPage;
